import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import api, { BASE_URL, fotoUrl } from '../api';
import { useTheme } from '../contexts/ThemeContext';

// ─── Tipler ───────────────────────────────────────────────────────────────────
interface Ders {
  ders_id: string; ders_adi: string; seviye: string;
  kalan_hak: number; birim_fiyat: number;
  satin_alinan_hak: number; kullanilan_hak: number;
  donduruldu: boolean;
}
interface Yoklama { tarih: string; ders_adi: string; durum: 'katildi' | 'gelmedi' | string; oturum_id: string; }
interface KrediIslem { id: string; olusturuldu: string; tutar: number; islem_turu: 'yukleme'|'harcama'|'iade'; aciklama: string; }
interface Seans { gun: string; baslangic_saat: string; bitis_saat: string; }
interface DersProgrami { id: string; ad: string; seanslar: Seans[]; }

// ─── Sabitler ─────────────────────────────────────────────────────────────────
const GUNLER: { kisalt: string; tam: string }[] = [
  { kisalt:'Pzt', tam:'Pazartesi' }, { kisalt:'Sal', tam:'Salı' },
  { kisalt:'Çar', tam:'Çarşamba' }, { kisalt:'Per', tam:'Perşembe' },
  { kisalt:'Cum', tam:'Cuma' },     { kisalt:'Cmt', tam:'Cumartesi' },
  { kisalt:'Paz', tam:'Pazar' },
];
const SAAT = (s: string) => s?.slice(0, 5) ?? '';
const TARIH = (s: string) => new Date(s).toLocaleDateString('tr-TR', { day:'2-digit', month:'short', year:'numeric' });

const SEVİYE_RENK: Record<string, [string, string, string]> = {
  'Başlangıç': ['#E6F1FB', 'rgba(12,68,124,0.15)', '#0C447C'],
  'Orta':      ['#FAEEDA', 'rgba(133,79,11,0.15)',  '#854F0B'],
  'İleri':     ['#EAF3DE', 'rgba(59,109,17,0.15)',  '#3B6D11'],
};

// ─── Ana bileşen ──────────────────────────────────────────────────────────────
export default function ProfilimSayfasi() {
  const { kullanici } = useAuth();
  const id = kullanici?.id;
  const { isDark } = useTheme();

  const card      = isDark ? '#1a1a1a' : 'white';
  const cardAlt   = isDark ? '#1e1e1e' : '#fafafa';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f5f5f5';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a2e';
  const textSec   = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';
  const barBg     = isDark ? '#333' : '#f0f0f0';

  const [ogrenci, setOgrenci]   = useState<any>(null);
  const [kredi, setKredi]       = useState<{ bakiye: number; gecmis: KrediIslem[] }>({ bakiye: 0, gecmis: [] });
  const [program, setProgram]   = useState<DersProgrami[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [fotoYukleniyor, setFotoYukleniyor] = useState(false);
  const [aktifSekme, setAktifSekme] = useState<'dersler'|'yoklama'|'program'|'kredi'>('dersler');
  const [duzenleModal, setDuzenleModal] = useState(false);
  const [dersDetay, setDersDetay] = useState<any>(null);
  const [duzenleForm, setDuzenleForm] = useState({ ad:'', soyad:'', tel:'' });
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [formHata, setFormHata] = useState('');
  const [icalUrl, setIcalUrl] = useState<string | null>(null);

  const yukle = async () => {
    if (!id) return;
    setYukleniyor(true);
    try {
      const [oRes, kRes, dRes, icalRes] = await Promise.all([
        api.get(`/api/ogrenciler/${id}`),
        api.get(`/api/krediler/${id}`),
        api.get('/api/dersler'),
        api.get('/api/takvim/ical-token/ogrenci').catch(() => null),
      ]);
      if (icalRes?.data?.token) {
        setIcalUrl(`${BASE_URL}/api/takvim/ical/ogrenci/${icalRes.data.ogrenci_id}?token=${icalRes.data.token}`);
      }
      setOgrenci(oRes.data);
      setKredi(kRes.data);
      const kayitliIds = new Set((oRes.data.dersler ?? []).map((d: Ders) => d.ders_id));
      setProgram(dRes.data.filter((d: DersProgrami) => kayitliIds.has(d.id)));
    } catch (e) { console.error(e); }
    finally { setYukleniyor(false); }
  };

  useEffect(() => { yukle(); }, [id]);

  const fotoYukle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFotoYukleniyor(true);
    const fd = new FormData();
    fd.append('foto', file);
    try { await api.post(`/api/ogrenciler/${id}/foto`, fd); yukle(); }
    catch { alert('Fotoğraf yüklenemedi.'); }
    finally { setFotoYukleniyor(false); }
  };

  const duzenleAc = () => {
    setDuzenleForm({ ad: ogrenci.ad ?? '', soyad: ogrenci.soyad ?? '', tel: ogrenci.tel ?? '' });
    setFormHata('');
    setDuzenleModal(true);
  };

  const duzenleKaydet = async () => {
    if (!duzenleForm.ad.trim() || !duzenleForm.soyad.trim()) { setFormHata('Ad ve soyad zorunlu'); return; }
    setKaydediliyor(true); setFormHata('');
    try {
      await api.patch(`/api/ogrenciler/${id}/kisisel`, duzenleForm);
      setDuzenleModal(false);
      yukle();
    } catch (e: any) { setFormHata(e.response?.data?.hata ?? 'Hata oluştu'); }
    finally { setKaydediliyor(false); }
  };

  if (yukleniyor) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', color: textMuted, fontSize:14 }}>
      Yükleniyor...
    </div>
  );
  if (!ogrenci) return (
    <div style={{ padding:24, color: textMuted }}>Profil bulunamadı.</div>
  );

  const dersler: Ders[]     = ogrenci.dersler ?? [];
  const yoklama: Yoklama[]  = ogrenci.yoklama_gecmisi ?? [];
  const katilimOrani = yoklama.length
    ? Math.round(yoklama.filter(y => y.durum === 'katildi').length / yoklama.length * 100) : null;

  const [sevBgLight, sevBgDark, sevRenk] = SEVİYE_RENK[ogrenci.seviye] ?? ['#f0f0f0', '#333', '#666'];
  const sevBg = isDark ? sevBgDark : sevBgLight;

  const inp: React.CSSProperties = {
    width:'100%', padding:'8px 10px', borderRadius:6, border:`1px solid ${isDark ? '#333' : '#ddd'}`,
    fontSize:13, boxSizing:'border-box', background: card, color: textPrimary,
  };

  return (
    <div style={{ padding: '24px 24px 40px', maxWidth: 720, margin: '0 auto' }}>

      {/* ── Profil kartı ── */}
      <div style={{ background: card, borderRadius:12, padding:24, marginBottom:16, border:`1px solid ${border}`, display:'flex', alignItems:'center', gap:20 }}>
        {/* Fotoğraf */}
        <div style={{ position:'relative', flexShrink:0 }}>
          <div
            onClick={() => !fotoYukleniyor && document.getElementById('foto-inp')?.click()}
            style={{ cursor:'pointer', borderRadius:'50%', overflow:'hidden', width:72, height:72 }}
            title="Fotoğrafı değiştir"
          >
            {ogrenci.foto_url
              ? <img src={fotoUrl(ogrenci.foto_url)} alt="" style={{ width:72, height:72, objectFit:'cover' }} />
              : <div style={{ width:72, height:72, background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, fontWeight:700, color:'#534AB7' }}>
                  {ogrenci.ad?.charAt(0)}{ogrenci.soyad?.charAt(0)}
                </div>
            }
            <div style={{ position:'absolute', inset:0, borderRadius:'50%', background:'rgba(0,0,0,0.35)',
              display:'flex', alignItems:'center', justifyContent:'center',
              opacity: fotoYukleniyor ? 1 : 0, transition:'opacity .2s' }}
              onMouseEnter={e => (e.currentTarget.style.opacity='1')}
              onMouseLeave={e => !fotoYukleniyor && (e.currentTarget.style.opacity='0')}>
              <span style={{ color:'white', fontSize: fotoYukleniyor ? 10 : 18 }}>
                {fotoYukleniyor ? 'Yükleniyor' : '✎'}
              </span>
            </div>
          </div>
          <input id="foto-inp" type="file" accept="image/*" style={{ display:'none' }} onChange={fotoYukle} />
        </div>

        {/* Bilgiler */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:20, fontWeight:700, color: textPrimary }}>{ogrenci.ad} {ogrenci.soyad}</div>
          <div style={{ fontSize:13, color: textSec, marginTop:2 }}>
            {ogrenci.email}{ogrenci.tel ? ` · ${ogrenci.tel}` : ''}
          </div>
          <div style={{ marginTop:8, display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ background: sevBg, color: sevRenk, borderRadius:20, padding:'3px 12px', fontSize:12, fontWeight:600 }}>
              {ogrenci.seviye}
            </span>
            <span style={{
              background: ogrenci.durum==='aktif' ? (isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE') : (isDark ? '#252525' : '#f0f0f0'),
              color: ogrenci.durum==='aktif' ? '#3B6D11' : textSec,
              borderRadius:20, padding:'3px 12px', fontSize:12,
            }}>
              {ogrenci.durum==='aktif' ? 'Aktif' : 'Pasif'}
            </span>
            <button onClick={duzenleAc}
              style={{ border:`1px solid ${border}`, background: card, borderRadius:20, padding:'3px 12px', fontSize:12, cursor:'pointer', color:'#534AB7' }}>
              ✎ Düzenle
            </button>
          </div>
        </div>

        {/* Kredi özet */}
        <div style={{ textAlign:'right', flexShrink:0 }}>
          <div style={{ fontSize:11, color: textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Kredi</div>
          <div style={{ fontSize:24, fontWeight:700, color: kredi.bakiye > 0 ? '#1D9E75' : textMuted }}>
            ₺{kredi.bakiye.toLocaleString('tr-TR')}
          </div>
        </div>
      </div>

      {/* ── Sekmeler ── */}
      <div style={{ display:'flex', gap:4, background: card, borderRadius:10, padding:4, marginBottom:16, border:`1px solid ${border}` }}>
        {([
          { key:'dersler', label:'Derslerim', badge: dersler.length || undefined },
          { key:'program', label:'Program',   badge: undefined },
          { key:'yoklama', label:'Yoklama',   badge: katilimOrani !== null ? `%${katilimOrani}` : undefined },
          { key:'kredi',   label:'Kredi geçmişi', badge: undefined },
        ] as const).map(s => (
          <button key={s.key} onClick={() => setAktifSekme(s.key)}
            style={{ flex:1, padding:'8px 4px', borderRadius:7, border:'none', cursor:'pointer', fontSize:13, fontWeight: aktifSekme===s.key ? 600 : 400,
              background: aktifSekme===s.key ? '#534AB7' : 'transparent',
              color:      aktifSekme===s.key ? 'white' : textSec }}>
            {s.label}{s.badge !== undefined ? <span style={{ marginLeft:6, background: aktifSekme===s.key ? 'rgba(255,255,255,0.25)' : (isDark ? '#333' : '#eee'), borderRadius:20, padding:'1px 7px', fontSize:11 }}>{s.badge}</span> : null}
          </button>
        ))}
      </div>

      {/* ── Derslerim ── */}
      {aktifSekme === 'dersler' && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {dersler.length === 0 ? (
            <Bos mesaj="Henüz hiçbir derse kayıtlı değilsin." isDark={isDark} border={border} card={card} />
          ) : dersler.map(d => {
            const renk = d.donduruldu
              ? isDark ? ['rgba(12,68,124,0.15)','#90BDE0'] : ['#E6F1FB','#0C447C']
              : d.kalan_hak <= 0
              ? isDark ? ['rgba(163,45,45,0.15)','#FCA5A5'] : ['#FCEBEB','#A32D2D']
              : d.kalan_hak <= 3
              ? isDark ? ['rgba(133,79,11,0.15)','#FCD34D'] : ['#FAEEDA','#854F0B']
              : isDark ? ['rgba(59,109,17,0.15)','#6EE7B7'] : ['#EAF3DE','#3B6D11'];
            const deger = (d.kalan_hak ?? 0) * (parseFloat(String(d.birim_fiyat)) || 0);
            return (
              <div key={d.ders_id} style={{ background: card, border:`1px solid ${border}`, borderRadius:10, padding:'14px 16px' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:15, color: textPrimary }}>{d.ders_adi}</div>
                    <div style={{ fontSize:12, color: textSec, marginTop:3 }}>
                      Birim: ₺{parseFloat(String(d.birim_fiyat)).toLocaleString('tr-TR')}
                      {' · '}Toplam değer: ₺{deger.toLocaleString('tr-TR')}
                    </div>
                  </div>
                  <span style={{ background:renk[0], color:renk[1], borderRadius:20, padding:'4px 12px', fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}>
                    {d.donduruldu ? '❄ Donduruldu' : `${d.kalan_hak} hak kaldı`}
                  </span>
                </div>
                {!d.donduruldu && (d.satin_alinan_hak ?? 0) > 0 && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ background: barBg, borderRadius:4, height:5, overflow:'hidden' }}>
                      <div style={{ height:'100%', borderRadius:4, background: renk[1],
                        width:`${Math.min(100, ((d.kalan_hak ?? 0) / (d.satin_alinan_hak ?? 1)) * 100)}%`,
                        transition:'width .4s' }} />
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', marginTop:4, fontSize:10, color: textMuted }}>
                      <span>{d.kullanilan_hak ?? 0} kullanıldı</span>
                      <span>{d.satin_alinan_hak ?? 0} toplam</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Program ── */}
      {aktifSekme === 'program' && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {program.length === 0 ? (
            <Bos mesaj="Aktif ders programın bulunamadı." isDark={isDark} border={border} card={card} />
          ) : GUNLER.flatMap(({ kisalt, tam }) => {
            const seanslar = program.flatMap(d =>
              (d.seanslar ?? []).filter(s => s.gun === kisalt).map(s => ({ ...s, ders: d }))
            ).sort((a, b) => a.baslangic_saat.localeCompare(b.baslangic_saat));
            if (!seanslar.length) return [];
            return [(
              <div key={kisalt} style={{ background: card, border:`1px solid ${border}`, borderRadius:10, overflow:'hidden' }}>
                <div style={{ background: cardAlt, borderBottom:`1px solid ${border}`, padding:'8px 14px', fontSize:12, fontWeight:600, color:'#534AB7' }}>{tam}</div>
                {seanslar.map((s, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderBottom: i < seanslar.length-1 ? `1px solid ${borderSub}` : 'none' }}>
                    <div style={{ fontSize:13, fontWeight:600, color: textPrimary, whiteSpace:'nowrap' }}>
                      {SAAT(s.baslangic_saat)} – {SAAT(s.bitis_saat)}
                    </div>
                    <div style={{ fontSize:13, color: textSec }}>{s.ders.ad}</div>
                  </div>
                ))}
              </div>
            )];
          })}
        </div>
      )}

      {/* ── Yoklama ── */}
      {aktifSekme === 'yoklama' && (
        <div>
          {katilimOrani !== null && (
            <div style={{ background: card, border:`1px solid ${border}`, borderRadius:10, padding:'14px 16px', marginBottom:12, display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, color: textSec, marginBottom:2 }}>Son {yoklama.length} derste katılım oranın</div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ background: barBg, borderRadius:4, height:8, flex:1, overflow:'hidden' }}>
                    <div style={{ height:'100%', borderRadius:4, background: katilimOrani>=80?'#1D9E75':katilimOrani>=60?'#F5A623':'#e24b4a', width:`${katilimOrani}%`, transition:'width .5s' }} />
                  </div>
                  <span style={{ fontSize:16, fontWeight:700, color: katilimOrani>=80?'#1D9E75':katilimOrani>=60?'#854F0B':'#A32D2D' }}>%{katilimOrani}</span>
                </div>
              </div>
              <div style={{ textAlign:'center', minWidth:60 }}>
                <div style={{ fontSize:20, fontWeight:700, color:'#1D9E75' }}>{yoklama.filter(y=>y.durum==='katildi').length}</div>
                <div style={{ fontSize:10, color: textMuted }}>katıldı</div>
              </div>
              <div style={{ textAlign:'center', minWidth:60 }}>
                <div style={{ fontSize:20, fontWeight:700, color:'#e24b4a' }}>{yoklama.filter(y=>y.durum==='gelmedi').length}</div>
                <div style={{ fontSize:10, color: textMuted }}>gelmedi</div>
              </div>
            </div>
          )}
          {yoklama.length === 0 ? (
            <Bos mesaj="Henüz yoklama kaydın yok." isDark={isDark} border={border} card={card} />
          ) : (
            <div style={{ background: card, border:`1px solid ${border}`, borderRadius:10, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background: cardAlt, borderBottom:`1px solid ${border}` }}>
                    {['Tarih','Ders','Durum'].map(h => (
                      <th key={h} style={{ textAlign:'left', padding:'9px 14px', fontSize:11, color: textMuted, fontWeight:600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {yoklama.map((y, i) => (
                    <tr key={i}
                      style={{ borderBottom: i < yoklama.length-1 ? `1px solid ${borderSub}` : 'none', cursor: 'pointer', transition: 'background .1s' }}
                      onClick={async () => {
                        const res = await api.get(`/api/yoklama/oturum/${y.oturum_id}`);
                        setDersDetay({ ...res.data, yoklama: y });
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = cardAlt)}
                      onMouseLeave={e => (e.currentTarget.style.background = card)}
                    >
                      <td style={{ padding:'9px 14px', color: textSec, whiteSpace:'nowrap' }}>{TARIH(y.tarih)}</td>
                      <td style={{ padding:'9px 14px', fontWeight:500, color: textPrimary }}>{y.ders_adi}</td>
                      <td style={{ padding:'9px 14px' }}>
                        <span style={{
                          background: y.durum==='katildi' ? (isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE')
                                    : y.durum==='gelmedi' ? (isDark ? 'rgba(163,45,45,0.15)' : '#FCEBEB')
                                    : (isDark ? 'rgba(12,68,124,0.15)' : '#E6F1FB'),
                          color: y.durum==='katildi'?'#3B6D11':y.durum==='gelmedi'?'#A32D2D':'#0C447C',
                          borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:600,
                        }}>{y.durum}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Kredi geçmişi ── */}
      {aktifSekme === 'kredi' && (
        <div>
          <div style={{ background: card, border:`1px solid ${border}`, borderRadius:10, padding:'16px 20px', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontSize:11, color: textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Mevcut bakiye</div>
              <div style={{ fontSize:28, fontWeight:700, color: kredi.bakiye>0?'#1D9E75': textMuted, marginTop:2 }}>
                ₺{kredi.bakiye.toLocaleString('tr-TR', { minimumFractionDigits:2 })}
              </div>
            </div>
            <button disabled
              style={{ padding:'9px 18px', borderRadius:8, border:`1px dashed ${isDark ? '#333' : '#ddd'}`, background: isDark ? '#1a1a1a' : '#fafafa', color: textMuted, cursor:'not-allowed', fontSize:13 }}>
              iyzico ile yükle &nbsp;<span style={{ fontSize:10, background: isDark ? '#252525' : '#eee', borderRadius:4, padding:'1px 6px' }}>Yakında</span>
            </button>
          </div>

          {kredi.gecmis.length === 0 ? (
            <Bos mesaj="Henüz kredi işlemi yok." isDark={isDark} border={border} card={card} />
          ) : (
            <div style={{ background: card, border:`1px solid ${border}`, borderRadius:10, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background: cardAlt, borderBottom:`1px solid ${border}` }}>
                    {['Tarih','Açıklama','Tutar','Tür'].map(h => (
                      <th key={h} style={{ textAlign: h==='Tutar'?'right':'left', padding:'9px 14px', fontSize:11, color: textMuted, fontWeight:600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kredi.gecmis.map((k, i) => (
                    <tr key={k.id} style={{ borderBottom: i<kredi.gecmis.length-1?`1px solid ${borderSub}`:'none' }}>
                      <td style={{ padding:'9px 14px', color: textSec, whiteSpace:'nowrap' }}>{TARIH(k.olusturuldu)}</td>
                      <td style={{ padding:'9px 14px', color: isDark ? '#ccc' : '#555' }}>{k.aciklama}</td>
                      <td style={{ padding:'9px 14px', textAlign:'right', fontWeight:600,
                        color: k.islem_turu==='harcama'?'#A32D2D':k.islem_turu==='iade'?'#0C447C':'#1D9E75' }}>
                        {k.islem_turu==='harcama'?'-':'+'} ₺{parseFloat(String(k.tutar)).toLocaleString('tr-TR', { minimumFractionDigits:2 })}
                      </td>
                      <td style={{ padding:'9px 14px' }}>
                        <span style={{
                          background: k.islem_turu==='harcama' ? (isDark ? 'rgba(163,45,45,0.15)' : '#FCEBEB')
                                    : k.islem_turu==='iade'    ? (isDark ? 'rgba(12,68,124,0.15)'  : '#E6F1FB')
                                    :                            (isDark ? 'rgba(59,109,17,0.15)'   : '#EAF3DE'),
                          color: k.islem_turu==='harcama'?'#A32D2D':k.islem_turu==='iade'?'#0C447C':'#3B6D11',
                          borderRadius:20, padding:'2px 8px', fontSize:11,
                        }}>{k.islem_turu}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── iCal bölümü ── */}
      {icalUrl && aktifSekme === 'program' && (
        <div style={{
          margin:'16px 0',
          background: isDark ? 'rgba(21,128,61,0.1)' : '#f0fdf4',
          border: isDark ? '1px solid rgba(21,128,61,0.3)' : '1px solid #86efac',
          borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'center', gap:12,
        }}>
          <span style={{ fontSize:20 }}>📅</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'#15803d', marginBottom:3 }}>Google Calendar'a Ekle</div>
            <div style={{ fontSize:11, color: isDark ? '#6EE7B7' : '#166534' }}>Derslerini Google, Apple veya Outlook takvimine eklemek için aşağıdaki URL'yi kopyala.</div>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(icalUrl); alert('URL kopyalandı! Takvim uygulamanızda "URL\'den ekle" seçeneğini kullanın.'); }}
            style={{ background:'#16a34a', color:'white', border:'none', borderRadius:8, padding:'8px 16px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}
          >
            URL Kopyala
          </button>
        </div>
      )}

      {/* ── Ders detay modalı ── */}
      {dersDetay && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:16 }}
          onClick={e => { if (e.target === e.currentTarget) setDersDetay(null); }}>
          <div style={{ background: card, borderRadius:14, width:'100%', maxWidth:480, maxHeight:'80vh', overflow:'hidden', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'18px 20px 14px', borderBottom:`1px solid ${border}`, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color: textPrimary }}>{dersDetay.yoklama.ders_adi}</div>
                <div style={{ fontSize:12, color: textSec, marginTop:3 }}>{TARIH(dersDetay.yoklama.tarih)}</div>
              </div>
              <button onClick={() => setDersDetay(null)} style={{ border:'none', background:'none', fontSize:20, color: textMuted, cursor:'pointer', lineHeight:1 }}>×</button>
            </div>

            <div style={{ overflowY:'auto', flex:1, padding:'16px 20px 20px' }}>
              {dersDetay.oturum.egitmen_notu ? (
                <div style={{ background: isDark ? 'rgba(83,74,183,0.15)' : '#EEEDFE', borderRadius:8, padding:'12px 14px', fontSize:13, color: isDark ? '#9d98e8' : '#534AB7', lineHeight:1.7 }}>
                  <div style={{ fontSize:10, fontWeight:700, color: isDark ? '#8B7FD4' : '#8B7FD4', marginBottom:6, letterSpacing:.4 }}>DERS NOTU</div>
                  {dersDetay.oturum.egitmen_notu}
                </div>
              ) : (
                <div style={{ color: textMuted, fontSize:13, textAlign:'center', paddingTop:12 }}>Bu ders için not girilmemiş.</div>
              )}

              <div style={{ marginTop:16, border:`1px dashed ${isDark ? '#333' : '#ddd'}`, borderRadius:8, padding:'24px 16px', textAlign:'center', color: textMuted, fontSize:13 }}>
                📹 Ders videosu — yakında
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Bilgi düzenleme modalı ── */}
      {duzenleModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:16 }}
          onClick={e => { if (e.target === e.currentTarget) setDuzenleModal(false); }}>
          <div style={{ background: card, borderRadius:12, padding:24, width:'100%', maxWidth:400 }}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:16, color: textPrimary }}>Kişisel bilgilerini güncelle</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <div>
                <div style={{ fontSize:11, color: textSec, marginBottom:4, fontWeight:500 }}>Ad</div>
                <input value={duzenleForm.ad} onChange={e => setDuzenleForm(p => ({ ...p, ad: e.target.value }))} style={inp} />
              </div>
              <div>
                <div style={{ fontSize:11, color: textSec, marginBottom:4, fontWeight:500 }}>Soyad</div>
                <input value={duzenleForm.soyad} onChange={e => setDuzenleForm(p => ({ ...p, soyad: e.target.value }))} style={inp} />
              </div>
            </div>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, color: textSec, marginBottom:4, fontWeight:500 }}>Telefon</div>
              <input value={duzenleForm.tel} onChange={e => setDuzenleForm(p => ({ ...p, tel: e.target.value }))}
                placeholder="+90 5xx xxx xx xx" style={inp} />
            </div>
            <div style={{ background: isDark ? '#252525' : '#f8f8f8', borderRadius:6, padding:'8px 12px', fontSize:12, color: textSec, marginBottom:16 }}>
              E-posta Google hesabından geliyor, buradan değiştirilemez.
            </div>
            {formHata && <div style={{ background: isDark ? 'rgba(163,45,45,0.15)' : '#FCEBEB', color:'#A32D2D', padding:'8px 12px', borderRadius:6, fontSize:12, marginBottom:12 }}>{formHata}</div>}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setDuzenleModal(false)}
                style={{ padding:'8px 16px', borderRadius:6, border:`1px solid ${border}`, background: card, cursor:'pointer', fontSize:13, color: textPrimary }}>
                İptal
              </button>
              <button onClick={duzenleKaydet} disabled={kaydediliyor}
                style={{ padding:'8px 20px', borderRadius:6, border:'none', background:'#534AB7', color:'white', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Yardımcı bileşenler ──────────────────────────────────────────────────────
function Bos({ mesaj, isDark, border, card }: { mesaj: string; isDark: boolean; border: string; card: string }) {
  const textMuted = isDark ? '#666' : '#bbb';
  return (
    <div style={{ background: card, border: `1px dashed ${border}`, borderRadius:10, padding:'36px 24px', textAlign:'center', color: textMuted, fontSize:13 }}>
      {mesaj}
    </div>
  );
}
